import React, { Fragment } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  Tooltip,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  LocalLibrary as LocalLibraryIcon,
  MenuBook as MenuBookIcon,
  Logout as LogoutIcon,
  Person2 as Person2Icon,
  ManageAccounts as ManageAccountsIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  ExpandLess,
  ExpandMore,
  Forum as ForumIcon,
  Mic as MicIcon,
  Class as ClassIcon,
  School as SchoolIcon,
  Spellcheck as SpellcheckIcon,
  Assignment as AssignmentIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  LibraryMusic as LibraryMusicIcon,
  Quiz as QuizIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  RocketLaunch as RocketLaunchIcon,
  AutoAwesome as AutoAwesomeIcon,
  Whatshot as WhatshotIcon,
  SpaceDashboardRounded as SpaceDashboardIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import clsx from "clsx";
import moment from "moment";
import { NavigateFunction } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { NotificationBell } from "@/components/NotificationBell";
import { ROUTES, appVersion } from "@/constants";
import { getUrlServer, isPremiumUser } from "@/helpers";
import { useGetDashboardOverview } from "@/services-react-query/queries/dashboard.query";

export interface INavItem {
  title: string;
  icon: React.ReactNode;
  path?: string;
  isPremiumOnly?: boolean;
  accentColor?: string;
  children?: {
    title: string;
    icon: React.ReactNode;
    path: string;
    isPremiumOnly?: boolean;
  }[];
}

interface IUserSidebarContentProps {
  currentUser: any;
  isDark: boolean;
  open: boolean;
  isMobile: boolean;
  handleDrawerClose: () => void;
  toggleTheme: () => void;
  handleLogout: () => void;
  navigate: NavigateFunction;
  isItemActive: (item: any) => boolean;
  openMenus: Record<string, boolean>;
  handleItemClick: (item: any) => void;
}

export const UserSidebarContent: React.FC<IUserSidebarContentProps> = ({
  currentUser,
  isDark,
  handleDrawerClose,
  toggleTheme,
  handleLogout,
  navigate,
  isItemActive,
  openMenus,
  handleItemClick,
}) => {
  const theme = useTheme();
  const isPremium = isPremiumUser(currentUser?.data);

  // Fetch streak & overview data from react-query
  const { data: overview } = useGetDashboardOverview();
  const streak = overview?.currentStreak || 0;
  const hasLearnedToday = overview?.hasLearnedToday ?? false;

  // Modern Gen Z navigation hierarchy with distinct skill accent colors
  const skillNavItems: INavItem[] = [
    {
      title: "Speaking",
      icon: <ForumIcon />,
      accentColor: "#4F46E5", // Electric Indigo
      children: [
        {
          title: "Phát triển ý tưởng",
          icon: <LocalLibraryIcon />,
          path: ROUTES.LEARN,
        },
        {
          title: "Ứng dụng ngữ pháp",
          icon: <MenuBookIcon />,
          path: ROUTES.LEARN_GRAMMAR,
        },
        {
          title: "Nâng cấp từ vựng",
          icon: <SpellcheckIcon />,
          path: ROUTES.WORDBANK,
        },
        {
          title: "Luyện tập phát âm",
          icon: <MicIcon />,
          path: ROUTES.ASSESSMENT_PRONUNCIATION,
        },
        {
          title: "Kiểm tra",
          icon: <AssignmentIcon />,
          path: ROUTES.ASSESSMENT_QUESTION,
        },
      ],
    },
    {
      title: "Reading",
      icon: <MenuBookIcon />,
      accentColor: "#6366F1", // Indigo
      isPremiumOnly: true,
      children: [
        {
          title: "Đọc luyện đề",
          icon: <MenuBookIcon />,
          path: ROUTES.IELTS_EXAMS_READING,
        },
        {
          title: "Đọc dạng bài",
          icon: <QuizIcon />,
          path: ROUTES.READING_PRACTICE,
        },
      ],
    },
    {
      title: "Listening",
      icon: <LibraryMusicIcon />,
      accentColor: "#EC4899", // Pink / Fuchsia
      isPremiumOnly: true,
      children: [
        {
          title: "Luyện đề",
          icon: <LibraryMusicIcon />,
          path: ROUTES.IELTS_EXAMS_LISTENING,
        },
        {
          title: "Luyện tập nghe",
          icon: <QuizIcon />,
          path: ROUTES.LISTENING_PRACTICE,
        },
      ],
    },
    {
      title: "Writing",
      icon: <EditIcon />,
      accentColor: "#F59E0B", // Amber
      path: ROUTES.IELTS_EXAMS_WRITING,
      isPremiumOnly: true,
    },
  ];

  const secondaryNavItems: INavItem[] = [
    {
      title: "Lớp học & Bài tập",
      icon: <SchoolIcon />,
      path: ROUTES.STUDENT_ASSIGNMENTS,
    },
    {
      title: "Bài đã học",
      icon: <Person2Icon />,
      path: ROUTES.PROFILE,
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowX: "hidden",
        overflowY: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {/* 1. Header with BrandLogo and Collapse button */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "16px 14px",
          justifyContent: "space-between",
          borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.06)"}`,
        }}
      >
        <Box
          onClick={() => navigate(ROUTES.DASHBOARD)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            transition: "opacity 0.2s ease",
            "&:hover": { opacity: 0.85 },
          }}
        >
          <BrandLogo isDark={isDark} />
        </Box>
        <IconButton
          onClick={handleDrawerClose}
          size="small"
          sx={{
            color: isDark ? "rgba(255, 255, 255, 0.5)" : "rgba(15, 23, 42, 0.5)",
            borderRadius: "10px",
            bgcolor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
            "&:hover": {
              bgcolor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.08)",
            },
          }}
        >
          {theme.direction === "ltr" ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* 2. Learner Identity Profile Header (Gamer / Gen Z Vibe) */}
      <Box sx={{ p: "12px 12px 8px 12px" }}>
        <Box
          onClick={() => navigate(ROUTES.ACCOUNT)}
          sx={{
            p: "10px 12px",
            borderRadius: "16px",
            cursor: "pointer",
            position: "relative",
            overflow: "hidden",
            background: isDark
              ? "linear-gradient(145deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.8) 100%)"
              : "linear-gradient(145deg, rgba(241, 245, 249, 0.9) 0%, rgba(255, 255, 255, 0.9) 100%)",
            border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(226, 232, 240, 0.8)"}`,
            boxShadow: isDark ? "0 4px 14px rgba(0, 0, 0, 0.25)" : "0 2px 8px rgba(0, 0, 0, 0.04)",
            transition: "all 0.25s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              borderColor: isDark ? "rgba(99, 102, 241, 0.4)" : "rgba(79, 70, 229, 0.4)",
              boxShadow: isDark ? "0 6px 18px rgba(99, 102, 241, 0.18)" : "0 4px 12px rgba(79, 70, 229, 0.1)",
            },
          }}
        >
          {/* Ambient Corner Glow */}
          <Box
            sx={{
              position: "absolute",
              top: "-20px",
              right: "-20px",
              width: "55px",
              height: "55px",
              borderRadius: "50%",
              background: isDark
                ? "radial-gradient(circle, rgba(99, 102, 241, 0.25) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(79, 70, 229, 0.15) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {/* Glowing Avatar */}
            <Tooltip title="Xem thông tin tài khoản" arrow placement="top">
              <Box sx={{ position: "relative" }}>
                <Avatar
                  sx={{
                    height: "42px",
                    width: "42px",
                    border: `2px solid ${isDark ? "#818CF8" : "#4F46E5"}`,
                    boxShadow: isDark ? "0 0 10px rgba(99, 102, 241, 0.35)" : "0 0 8px rgba(79, 70, 229, 0.25)",
                    backgroundColor: isDark ? "#1E293B" : "#E2E8F0",
                  }}
                  src={
                    currentUser?.data?.avatarPath
                      ? `${getUrlServer(currentUser?.data?.avatarPath)}`
                      : "/images/people.png"
                  }
                />
                {/* Online status pulse */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "1px",
                    right: "1px",
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    bgcolor: "#10B981",
                    border: `2px solid ${isDark ? "#0F172A" : "#FFFFFF"}`,
                  }}
                />
              </Box>
            </Tooltip>

            {/* User Meta */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.25 }}>
                <Typography
                  sx={{
                    color: isDark ? "#F8FAFC" : "#0F172A",
                    fontWeight: 800,
                    fontSize: "13.5px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "95px",
                  }}
                >
                  {currentUser?.data?.username || "Học viên"}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {isPremium ? (
                    <Chip
                      label="PRO 👑"
                      size="small"
                      sx={{
                        height: "18px",
                        fontSize: "9.5px",
                        fontWeight: 900,
                        background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
                        color: "#FFFFFF",
                        borderRadius: "100px",
                        boxShadow: "0 2px 6px rgba(245, 158, 11, 0.35)",
                        "& .MuiChip-label": { px: "5px" },
                      }}
                    />
                  ) : (
                    <Chip
                      label="MEMBER"
                      size="small"
                      sx={{
                        height: "17px",
                        fontSize: "9px",
                        fontWeight: 700,
                        bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                        color: isDark ? "#94A3B8" : "#64748B",
                        borderRadius: "100px",
                        "& .MuiChip-label": { px: "4px" },
                      }}
                    />
                  )}
                  <NotificationBell />
                </Box>
              </Box>

              {/* Gamified Streak Flame Pill */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.2 }}>
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    streak > 0
                      ? hasLearnedToday
                        ? `Tuyệt vời! Bạn đã hoàn thành bài học hôm nay. Chuỗi ${streak} ngày đang bùng cháy 🔥`
                        : `Chuỗi ${streak} ngày đang chờ bạn! Hãy hoàn thành 1 bài học hôm nay để duy trì chuỗi.`
                      : "Bắt đầu bài học đầu tiên hôm nay để thắp sáng chuỗi streak!"
                  }
                >
                  <Box
                    component={motion.div}
                    whileHover={{ scale: 1.05 }}
                    sx={{
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.4,
                      px: "6px",
                      py: "1px",
                      borderRadius: "100px",
                      background:
                        streak > 0
                          ? hasLearnedToday
                            ? isDark
                              ? "linear-gradient(90deg, rgba(249, 115, 22, 0.25) 0%, rgba(239, 68, 68, 0.25) 100%)"
                              : "linear-gradient(90deg, rgba(249, 115, 22, 0.15) 0%, rgba(239, 68, 68, 0.15) 100%)"
                            : isDark
                              ? "rgba(245, 158, 11, 0.12)"
                              : "rgba(245, 158, 11, 0.1)"
                          : isDark
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.04)",
                      border: `1px solid ${
                        streak > 0
                          ? hasLearnedToday
                            ? "rgba(249, 115, 22, 0.4)"
                            : "rgba(245, 158, 11, 0.35)"
                          : isDark
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.06)"
                      }`,
                    }}
                  >
                    <WhatshotIcon
                      sx={{
                        fontSize: 13,
                        color: streak > 0 ? (hasLearnedToday ? "#F97316" : "#F59E0B") : isDark ? "#64748B" : "#94A3B8",
                        filter: streak > 0 && hasLearnedToday ? "drop-shadow(0 0 4px rgba(249, 115, 22, 0.6))" : "none",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "10.5px",
                        fontWeight: 800,
                        color:
                          streak > 0
                            ? hasLearnedToday
                              ? isDark
                                ? "#FB923C"
                                : "#EA580C"
                              : isDark
                                ? "#FCD34D"
                                : "#D97706"
                            : isDark
                              ? "#94A3B8"
                              : "#64748B",
                        letterSpacing: "0.01em",
                      }}
                    >
                      {streak > 0
                        ? hasLearnedToday
                          ? `${streak} ngày 🔥`
                          : `${streak} ngày (chờ học)`
                        : "Chưa có chuỗi"}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 3. Gamified AI Energy Widget ("Năng Lượng AI") */}
      {(() => {
        const hasGroup = !!currentUser?.data?.group;
        const toppedUpBalance = currentUser?.data?.toppedUpBalance ?? 0;

        if (!isPremium && !hasGroup) {
          return (
            <Box sx={{ px: "12px", mb: 1 }}>
              <Tooltip title="Nhấn để xem thông tin tài khoản & nạp năng lượng" arrow placement="right">
                <Box
                  onClick={() => navigate(ROUTES.ACCOUNT)}
                  sx={{
                    p: "9px 12px",
                    borderRadius: "14px",
                    bgcolor: isDark ? "rgba(30, 41, 59, 0.5)" : "rgba(241, 245, 249, 0.7)",
                    border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(226, 232, 240, 0.8)"}`,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      borderColor: isDark ? "rgba(56, 189, 248, 0.4)" : "rgba(2, 132, 199, 0.4)",
                      boxShadow: isDark ? "0 4px 12px rgba(56, 189, 248, 0.15)" : "0 4px 12px rgba(2, 132, 199, 0.08)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <AutoAwesomeIcon sx={{ fontSize: 13, color: isDark ? "#38BDF8" : "#0284C7" }} />
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: isDark ? "rgba(255, 255, 255, 0.85)" : "#334155",
                        }}
                      >
                        Năng lượng AI
                      </Typography>
                    </Box>
                    <Chip
                      label="Free Quota"
                      size="small"
                      sx={{
                        height: "17px",
                        fontSize: "9px",
                        fontWeight: 700,
                        bgcolor: isDark ? "rgba(56, 189, 248, 0.15)" : "rgba(2, 132, 199, 0.1)",
                        color: isDark ? "#38BDF8" : "#0284C7",
                        borderRadius: "100px",
                        "& .MuiChip-label": { px: "5px" },
                      }}
                    />
                  </Box>
                  {toppedUpBalance > 0 ? (
                    <Box sx={{ mt: 0.6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography sx={{ fontSize: "10px", color: isDark ? "rgba(255,255,255,0.5)" : "#64748B" }}>
                        Số dư nạp:
                      </Typography>
                      <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#10B981" }}>
                        {toppedUpBalance.toLocaleString("vi-VN")} đ
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        mt: 0.4,
                        fontSize: "9.5px",
                        color: isDark ? "rgba(255, 255, 255, 0.4)" : "#94A3B8",
                        fontStyle: "italic",
                      }}
                    >
                      Gói miễn phí cơ bản
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            </Box>
          );
        }

        const limit =
          currentUser?.data?.monthlyQuotaLimit ??
          currentUser?.data?.group?.monthlyQuotaLimit ??
          currentUser?.data?.currentPackage?.monthlyQuota ??
          130000;
        const used = currentUser?.data?.monthlyQuotaUsed ?? 0;
        const remainingQuota = Math.max(0, limit - used);
        const quotaPercent = limit > 0 ? Math.round((remainingQuota / limit) * 100) : 0;
        const toppedUpPercent = limit > 0 ? Math.round((toppedUpBalance / limit) * 100) : 0;
        const isExhausted = remainingQuota <= 0 && toppedUpBalance <= 0;

        let quotaGradient = "linear-gradient(90deg, #4F46E5 0%, #10B981 100%)";
        let quotaPctColor = "#10B981";

        if (quotaPercent < 20) {
          quotaGradient = "linear-gradient(90deg, #EF4444 0%, #F87171 100%)";
          quotaPctColor = "#EF4444";
        } else if (quotaPercent < 50) {
          quotaGradient = "linear-gradient(90deg, #F59E0B 0%, #FBBF24 100%)";
          quotaPctColor = "#F59E0B";
        }

        const nextQuotaRefreshDate = currentUser?.data?.nextQuotaRefreshDate;
        const isValidRefreshDate = Boolean(nextQuotaRefreshDate && moment(nextQuotaRefreshDate).isValid());
        const resetDateStr = isValidRefreshDate ? moment(nextQuotaRefreshDate).format("DD/MM") : "";

        return (
          <Box sx={{ px: "12px", mb: 1 }}>
            <Tooltip title="Nhấn để xem chi tiết năng lượng & hồ sơ tài khoản" arrow placement="right">
              <Box
                onClick={() => navigate(ROUTES.ACCOUNT)}
                sx={{
                  p: "9px 12px",
                  borderRadius: "14px",
                  bgcolor: isDark ? "rgba(30, 41, 59, 0.5)" : "rgba(241, 245, 249, 0.7)",
                  border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(226, 232, 240, 0.8)"}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    borderColor: isDark ? "rgba(99, 102, 241, 0.4)" : "rgba(79, 70, 229, 0.4)",
                    boxShadow: isDark ? "0 4px 12px rgba(99, 102, 241, 0.15)" : "0 4px 12px rgba(79, 70, 229, 0.08)",
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 13, color: quotaPctColor }} />
                    <Typography
                      sx={{
                        fontSize: "11px",
                        fontWeight: 800,
                        color: isDark ? "rgba(255, 255, 255, 0.9)" : "#1E293B",
                      }}
                    >
                      Năng lượng AI
                    </Typography>
                  </Box>
                  {isExhausted ? (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(ROUTES.ACCOUNT);
                      }}
                      startIcon={<RocketLaunchIcon sx={{ fontSize: "10px !important" }} />}
                      sx={{
                        px: "8px",
                        py: "1px",
                        minWidth: "auto",
                        height: "18px",
                        fontSize: "9px",
                        fontWeight: 800,
                        color: "#fff",
                        background: "linear-gradient(90deg, #EF4444 0%, #F87171 100%)",
                        borderRadius: "100px",
                        boxShadow: "0 2px 6px rgba(239, 68, 68, 0.35)",
                        textTransform: "none",
                        lineHeight: 1.2,
                      }}
                    >
                      Nạp ngay
                    </Button>
                  ) : (
                    <Typography sx={{ fontSize: "10px", fontWeight: 800, color: quotaPctColor }}>
                      {quotaPercent}%
                    </Typography>
                  )}
                </Box>

                {/* Progress Track */}
                <Box
                  sx={{
                    width: "100%",
                    height: "5px",
                    borderRadius: "100px",
                    bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    position: "relative",
                    mb: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      height: "100%",
                      width: `${Math.min(100, quotaPercent)}%`,
                      background: quotaGradient,
                      borderRadius: "100px",
                      boxShadow: `0 0 8px ${quotaPctColor}80`,
                      transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
                    }}
                  />
                </Box>

                {resetDateStr && (
                  <Typography
                    sx={{
                      fontSize: "9.5px",
                      color: isDark ? "rgba(255, 255, 255, 0.45)" : "#64748B",
                      fontWeight: 600,
                    }}
                  >
                    Làm mới vào ngày {resetDateStr}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          </Box>
        );
      })()}

      {/* 4. Primary Navigation Menu */}
      <Box sx={{ px: "10px", flex: 1 }}>
        {/* Overview / Dashboard Navigation Item */}
        <List sx={{ p: 0, mb: 1 }}>
          {(() => {
            const overviewItem: INavItem = {
              title: "Tổng quan",
              icon: <SpaceDashboardIcon />,
              path: ROUTES.DASHBOARD,
              accentColor: "#4F46E5",
            };
            const isActive = isItemActive(overviewItem);
            const accent = overviewItem.accentColor || "#4F46E5";

            return (
              <ListItem
                disablePadding
                component={motion.div}
                whileHover={{ x: 2 }}
                transition={{ duration: 0.15 }}
                onClick={() => handleItemClick(overviewItem)}
                sx={{
                  display: "block",
                  borderRadius: "12px",
                  overflow: "hidden",
                  position: "relative",
                  bgcolor: isActive ? (isDark ? `${accent}22` : `${accent}14`) : "transparent",
                  border: `1px solid ${isActive ? (isDark ? `${accent}45` : `${accent}30`) : "transparent"}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
                  },
                }}
              >
                {isActive && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      top: "15%",
                      bottom: "15%",
                      width: "3.5px",
                      borderRadius: "0 4px 4px 0",
                      bgcolor: accent,
                      boxShadow: `0 0 8px ${accent}`,
                    }}
                  />
                )}

                <ListItemButton
                  sx={{
                    px: 1.5,
                    py: 1,
                    minHeight: "40px",
                    borderRadius: "12px",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: "30px",
                      color: isActive ? accent : isDark ? "rgba(255, 255, 255, 0.6)" : "#64748B",
                      "& svg": { fontSize: "20px" },
                    }}
                  >
                    {overviewItem.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={overviewItem.title}
                    primaryTypographyProps={{
                      fontSize: "13.5px",
                      fontWeight: isActive ? 800 : 600,
                      color: isActive
                        ? isDark
                          ? "#FFFFFF"
                          : "#0F172A"
                        : isDark
                          ? "rgba(255, 255, 255, 0.75)"
                          : "#334155",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })()}
        </List>

        <Typography
          sx={{
            px: 1,
            py: 0.5,
            fontSize: "11px",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: isDark ? "rgba(255, 255, 255, 0.4)" : "#94A3B8",
          }}
        >
          Kỹ năng chính
        </Typography>

        <List sx={{ p: 0 }}>
          {skillNavItems.map((item, index) => {
            const hasChildren = !!item.children;
            const isOpen = openMenus[item.title];
            const isActive = isItemActive(item);
            const isParentActive = hasChildren && item.children?.some((child) => isItemActive(child));
            const accent = item.accentColor || "#10B981";

            return (
              <Fragment key={index}>
                <ListItem
                  disablePadding
                  component={motion.div}
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => handleItemClick(item)}
                  sx={{
                    display: "block",
                    borderRadius: "12px",
                    mb: "3px",
                    overflow: "hidden",
                    position: "relative",
                    bgcolor: isActive || isParentActive ? (isDark ? `${accent}18` : `${accent}12`) : "transparent",
                    border: `1px solid ${
                      isActive || isParentActive ? (isDark ? `${accent}40` : `${accent}30`) : "transparent"
                    }`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
                    },
                  }}
                >
                  {/* Active Left Glow Accent Bar */}
                  {(isActive || isParentActive) && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: "15%",
                        bottom: "15%",
                        width: "3.5px",
                        borderRadius: "0 4px 4px 0",
                        bgcolor: accent,
                        boxShadow: `0 0 8px ${accent}`,
                      }}
                    />
                  )}

                  <ListItemButton
                    sx={{
                      px: 1.5,
                      py: 1,
                      minHeight: "40px",
                      borderRadius: "12px",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "30px",
                        color: isActive || isParentActive ? accent : isDark ? "rgba(255, 255, 255, 0.6)" : "#64748B",
                        "& svg": { fontSize: "20px" },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontSize: "13.5px",
                        fontWeight: isActive || isParentActive ? 800 : 600,
                        color:
                          isActive || isParentActive
                            ? isDark
                              ? "#FFFFFF"
                              : "#0F172A"
                            : isDark
                              ? "rgba(255, 255, 255, 0.75)"
                              : "#334155",
                      }}
                    />
                    {item.isPremiumOnly && !isPremium && (
                      <LockIcon
                        sx={{
                          fontSize: 15,
                          color: isDark ? "rgba(255,255,255,0.35)" : "#94A3B8",
                          ml: 1,
                        }}
                      />
                    )}
                    {hasChildren &&
                      (isOpen ? (
                        <ExpandLess sx={{ fontSize: 18, color: isDark ? "rgba(255,255,255,0.4)" : "#94A3B8" }} />
                      ) : (
                        <ExpandMore sx={{ fontSize: 18, color: isDark ? "rgba(255,255,255,0.4)" : "#94A3B8" }} />
                      ))}
                  </ListItemButton>
                </ListItem>

                {/* Submenu Accordion */}
                {hasChildren && (
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        position: "relative",
                        ml: "14px",
                        mb: "4px",
                        borderLeft: `1.5px solid ${isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(15, 23, 42, 0.1)"}`,
                        pl: "8px",
                      }}
                    >
                      {item.children?.map((child, childIdx) => {
                        const isChildActive = isItemActive(child);
                        return (
                          <ListItem
                            key={childIdx}
                            disablePadding
                            component={motion.div}
                            whileHover={{ x: 3 }}
                            transition={{ duration: 0.12 }}
                            onClick={() => handleItemClick(child)}
                            sx={{
                              borderRadius: "10px",
                              mb: "2px",
                              bgcolor: isChildActive ? (isDark ? `${accent}22` : `${accent}15`) : "transparent",
                              border: `1px solid ${
                                isChildActive ? (isDark ? `${accent}50` : `${accent}40`) : "transparent"
                              }`,
                              "&:hover": {
                                bgcolor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
                              },
                            }}
                          >
                            <ListItemButton sx={{ py: "6px", px: 1, minHeight: "34px", borderRadius: "10px" }}>
                              <ListItemIcon
                                sx={{
                                  minWidth: "24px",
                                  color: isChildActive ? accent : isDark ? "rgba(255, 255, 255, 0.5)" : "#94A3B8",
                                  "& svg": { fontSize: "17px" },
                                }}
                              >
                                {child.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={child.title}
                                primaryTypographyProps={{
                                  fontSize: "12.5px",
                                  fontWeight: isChildActive ? 700 : 500,
                                  color: isChildActive
                                    ? isDark
                                      ? "#FFFFFF"
                                      : "#0F172A"
                                    : isDark
                                      ? "rgba(255, 255, 255, 0.7)"
                                      : "#475569",
                                }}
                              />
                              {child.isPremiumOnly && !isPremium && (
                                <LockIcon
                                  sx={{
                                    fontSize: 14,
                                    color: isDark ? "rgba(255,255,255,0.3)" : "#94A3B8",
                                    ml: 0.5,
                                  }}
                                />
                              )}
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </Fragment>
            );
          })}
        </List>
      </Box>

      {/* 5. Modern Glassmorphic PRO Upgrade Banner (for Free Users) */}
      {!isPremium && (
        <Box sx={{ px: "12px", my: 1.5 }}>
          <Box
            component={motion.div}
            whileHover={{ y: -2 }}
            sx={{
              background: isDark
                ? "linear-gradient(135deg, rgba(99, 102, 241, 0.85) 0%, rgba(168, 85, 247, 0.85) 100%)"
                : "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
              borderRadius: "16px",
              p: "14px",
              color: "#FFFFFF",
              position: "relative",
              overflow: "hidden",
              boxShadow: isDark ? "0 8px 24px rgba(99, 102, 241, 0.35)" : "0 8px 20px rgba(79, 70, 229, 0.25)",
            }}
          >
            <RocketLaunchIcon
              sx={{
                position: "absolute",
                right: "-8px",
                bottom: "-10px",
                fontSize: "56px",
                opacity: 0.18,
                transform: "rotate(-10deg)",
              }}
            />
            <Typography sx={{ fontSize: "13px", fontWeight: 800, mb: 0.4 }}>Mở khóa PRO AI 🚀</Typography>
            <Typography sx={{ fontSize: "10.5px", opacity: 0.9, mb: 1.25, lineHeight: 1.35, fontWeight: 500 }}>
              Không giới hạn lượt nói AI & mở khóa toàn bộ đề thi IELTS.
            </Typography>
            <Button
              fullWidth
              size="small"
              variant="contained"
              onClick={() => navigate(ROUTES.UPGRADE)}
              sx={{
                backgroundColor: "#FFFFFF",
                color: "#4F46E5",
                fontSize: "11.5px",
                fontWeight: 800,
                textTransform: "none",
                borderRadius: "10px",
                py: "5px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": {
                  backgroundColor: "#F8FAFC",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                },
              }}
            >
              Nâng cấp ngay
            </Button>
          </Box>
        </Box>
      )}

      {/* 6. Secondary Navigation (Workspace / Profile / Class) */}
      <Box sx={{ px: "10px", mt: "auto" }}>
        <Typography
          sx={{
            px: 1,
            py: 0.5,
            fontSize: "11px",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: isDark ? "rgba(255, 255, 255, 0.4)" : "#94A3B8",
          }}
        >
          Không gian học tập
        </Typography>

        <List sx={{ p: 0 }}>
          {secondaryNavItems.map((item, index) => {
            const isActive = isItemActive(item);
            return (
              <ListItem
                key={index}
                disablePadding
                component={motion.div}
                whileHover={{ x: 2 }}
                onClick={() => handleItemClick(item)}
                sx={{
                  display: "block",
                  borderRadius: "10px",
                  mb: "2px",
                  bgcolor: isActive ? (isDark ? "rgba(45, 212, 191, 0.15)" : "rgba(13, 148, 136, 0.1)") : "transparent",
                  border: `1px solid ${
                    isActive ? (isDark ? "rgba(45, 212, 191, 0.3)" : "rgba(13, 148, 136, 0.25)") : "transparent"
                  }`,
                }}
              >
                <ListItemButton sx={{ px: 1.5, py: "7px", minHeight: "36px", borderRadius: "10px" }}>
                  <ListItemIcon
                    sx={{
                      minWidth: "28px",
                      color: isActive
                        ? isDark
                          ? "#2DD4BF"
                          : "#0D9488"
                        : isDark
                          ? "rgba(255, 255, 255, 0.5)"
                          : "#64748B",
                      "& svg": { fontSize: "18px" },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontSize: "12.5px",
                      fontWeight: isActive ? 800 : 500,
                      color: isActive
                        ? isDark
                          ? "#FFFFFF"
                          : "#0D9488"
                        : isDark
                          ? "rgba(255, 255, 255, 0.75)"
                          : "#475569",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* 7. Bottom Sleek Utility Bar (Theme switcher, Logout, Version) */}
      <Box
        sx={{
          borderTop: `1px solid ${isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(15, 23, 42, 0.06)"}`,
          p: "10px 12px 14px 12px",
          mt: 1,
        }}
      >
        {/* Theme Toggle Pill */}
        <Box
          component={motion.div}
          whileHover={{ scale: 1.01 }}
          onClick={toggleTheme}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            p: "7px 10px",
            borderRadius: "10px",
            mb: "6px",
            bgcolor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(15, 23, 42, 0.03)",
            border: `1px solid ${isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.06)"}`,
            "&:hover": {
              bgcolor: isDark ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.06)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isDark ? (
              <LightModeIcon sx={{ fontSize: "17px", color: "#FBBF24" }} />
            ) : (
              <DarkModeIcon sx={{ fontSize: "17px", color: "#6366F1" }} />
            )}
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: isDark ? "rgba(255, 255, 255, 0.7)" : "#475569",
              }}
            >
              {isDark ? "Giao diện sáng" : "Giao diện tối"}
            </Typography>
          </Box>
          <Chip
            label={isDark ? "Dark" : "Light"}
            size="small"
            sx={{
              height: "17px",
              fontSize: "9px",
              fontWeight: 700,
              bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
              color: isDark ? "#94A3B8" : "#64748B",
              borderRadius: "100px",
              "& .MuiChip-label": { px: "4px" },
            }}
          />
        </Box>

        {/* Logout Action */}
        <Box
          component={motion.div}
          whileHover={{ scale: 1.01 }}
          onClick={handleLogout}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            p: "7px 10px",
            borderRadius: "10px",
            color: isDark ? "rgba(255, 255, 255, 0.6)" : "#64748B",
            transition: "all 0.2s ease",
            "&:hover": {
              bgcolor: isDark ? "rgba(244, 63, 94, 0.12)" : "#FFF1F2",
              color: "#F43F5E",
              "& svg": { color: "#F43F5E" },
            },
          }}
        >
          <LogoutIcon sx={{ fontSize: "17px" }} />
          <Typography sx={{ fontSize: "12px", fontWeight: 600 }}>Đăng xuất</Typography>
        </Box>

        {/* Version */}
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Typography
            sx={{
              fontSize: "9.5px",
              color: isDark ? "rgba(255, 255, 255, 0.3)" : "#94A3B8",
              letterSpacing: "0.05em",
              fontWeight: 600,
            }}
          >
            v{appVersion}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default UserSidebarContent;
